// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconArrowFirst from '../../Icons/IconArrowFirst';
import IconArrowLast from '../../Icons/IconArrowLast';
import ExpandedRow from './ExpandedTable';
import { useAuthStore } from '../../store/AuthContext';
import NoData from '../../components/NoData';

const PAGE_SIZE = 10;

type Props = {
  data: DataCompra[];
  total: number;
  withPagination: boolean;
  isTicketDownloadable: boolean;
};

interface DataCompra {
  fecha: string;
  concepto: string;
  importe: number;
  ahorroTicket: number;
  idTicket: number;
  ingresoEroskiClub: number;
  ingresoEroskiClubOro: number;
  otrosDescuentos: number;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CustomPaginationTable: FC<Props> = ({ data, isTicketDownloadable }) => {
  const dataUser = useAuthStore();
  const { moneda } = dataUser.user;
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [sort, setSort] = useState<{
    key: keyof DataCompra;
    direction: 'asc' | 'desc';
  } | null>(null);

  const pageCount = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const firstRowIndex = (currentPage - 1) * PAGE_SIZE;
  const visibleData = [...data]
    .sort((a, b) => {
      if (!sort) return 0;

      const first = a[sort.key];
      const second = b[sort.key];

      if (sort.key === 'fecha') {
        if ((first as string).split('/')[2].length === 4) {
          const [day, month, year] = (first as string).split('/');
          const formattedFirst = `${year}-${month}-${day}`;
          const [day2, month2, year2] = (second as string).split('/');
          const formattedSecond = `${year2}-${month2}-${day2}`;
          const firstDate = Date.parse(formattedFirst);
          const secondDate = Date.parse(formattedSecond);
          return sort.direction === 'asc'
            ? firstDate - secondDate
            : secondDate - firstDate;
        }
        const firstDate = Date.parse(first as string);
        const secondDate = Date.parse(second as string);
        return sort.direction === 'asc'
          ? firstDate - secondDate
          : secondDate - firstDate;
      }

      const result =
        typeof first === 'number' && typeof second === 'number'
          ? first - second
          : String(first).localeCompare(String(second));

      return sort.direction === 'asc' ? result : -result;
    })
    .slice(firstRowIndex, firstRowIndex + PAGE_SIZE);

  const isExpandableRow = (row: DataCompra) => {
    return Boolean(row);
  };

  const expandRowComponent = (row: DataCompra) => {
    return (
      <ExpandedRow data={row} isTicketDownloadable={isTicketDownloadable} />
    );
  };

  const toggleExpandedRow = (row: DataCompra) => {
    if (!isExpandableRow(row)) {
      return;
    }

    setExpandedRowId((currentRowId) => {
      return currentRowId === row.idTicket ? null : row.idTicket;
    });
  };

  const toggleSort = (key: keyof DataCompra) => {
    setSort((current) => ({
      key,
      direction:
        current?.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPage(1);
  };

  const getAriaSort = (key: keyof DataCompra) => {
    if (sort?.key !== key) {
      return undefined;
    }

    return sort.direction === 'asc' ? 'ascending' : 'descending';
  };

  const getPageNumbers = () => {
    if (pageCount < 5) {
      return Array.from({ length: pageCount }, (_, index) => index + 1);
    }

    let start = currentPage - 2;
    let end = currentPage + 2;

    if (start < 1) {
      start = 1;
      end = 5;
    }

    if (end > pageCount) {
      end = pageCount;
      start = pageCount - 4;
    }

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };

  return (
    <>
      {/* Elemento oculto FirstPage para clonar y poder añadirlo al paginado */}
      <li id="first-page" className="page-item hidden">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a href="" className="page-link">
          <div className="icon icon-medium go-back">
            <IconArrowFirst />
          </div>
        </a>
      </li>

      {/* Elemento oculto LastPage para clonar y poder añadirlo al paginado */}
      <li id="last-page" className="page-item hidden">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a href="" className="page-link">
          <div className="icon icon-medium">
            <IconArrowLast />
          </div>
        </a>
      </li>
      {(() => {
        if (data !== null && data.length > 0) {
          return (
            <>
              <div className="table-responsive">
                <table className="table compras-table">
                  <caption className="sr-only">{t('compras.title')}</caption>
                  <thead>
                    <tr>
                      <th
                        className="th-fecha-compras"
                        scope="col"
                        aria-sort={getAriaSort('fecha')}
                      >
                        <button
                          type="button"
                          onClick={() => toggleSort('fecha')}
                        >
                          {t('compras.date')}
                        </button>
                      </th>
                      <th
                        className="th-concepto-compras"
                        scope="col"
                        aria-sort={getAriaSort('concepto')}
                      >
                        <button
                          type="button"
                          onClick={() => toggleSort('concepto')}
                        >
                          {t('compras.concept')}
                        </button>
                      </th>
                      <th
                        className="th-importe-compras"
                        scope="col"
                        aria-sort={getAriaSort('importe')}
                      >
                        <button
                          type="button"
                          onClick={() => toggleSort('importe')}
                        >
                          {t('compras.amount')}
                        </button>
                      </th>
                      <th
                        className="th-puntos-compras"
                        scope="col"
                        aria-sort={getAriaSort('ahorroTicket')}
                      >
                        <button
                          type="button"
                          onClick={() => toggleSort('ahorroTicket')}
                        >
                          {t('compras.savedTickets')}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleData.map((movement, index) => (
                      <>
                        <tr
                          key={`compras-movement-${firstRowIndex + index}`}
                          className={`tr-static row-${movement.idTicket}${
                            expandedRowId === movement.idTicket ? ' activa' : ''
                          }`}
                          onClick={() => toggleExpandedRow(movement)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              toggleExpandedRow(movement);
                            }
                          }}
                        >
                          <td className="td-fecha-compras">{movement.fecha}</td>
                          <td>{movement.concepto}</td>
                          <td className="td-importe-compras">
                            {`${movement.importe}${
                              moneda === 'euro' ? t('euro') : t('cc')
                            }`}
                          </td>
                          <td className="td-puntos-compras">
                            {`${movement.ahorroTicket}${
                              moneda === 'euro' ? t('euro') : t('cc')
                            }`}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 15"
                              className="row-expand-icon"
                            >
                              <polygon
                                fillRule="evenodd"
                                points="2.832 5 12 14.216 21.168 5 24 7.832 12 19.832 0 7.832"
                                transform="translate(0 -5)"
                              />
                            </svg>
                          </td>
                        </tr>
                        {expandedRowId === movement.idTicket && (
                          <tr className="detalle-fila">
                            {expandRowComponent(movement)}
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
              {pageCount > 1 && (
                <nav aria-label={t('compras.pagination')}>
                  <ul className="pagination">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? 'disabled' : ''
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        disabled={currentPage === 1}
                        onClick={() => setPage(currentPage - 1)}
                      >
                        {t('compras.pagination-previous')}
                      </button>
                    </li>
                    {getPageNumbers().map((pageNumber) => (
                      <li
                        className={`page-item ${
                          pageNumber === currentPage ? 'active' : ''
                        }`}
                        key={pageNumber}
                      >
                        <button
                          type="button"
                          className="page-link"
                          aria-current={
                            pageNumber === currentPage ? 'page' : undefined
                          }
                          onClick={() => setPage(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      </li>
                    ))}
                    <li
                      className={`page-item ${
                        currentPage === pageCount ? 'disabled' : ''
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        disabled={currentPage === pageCount}
                        onClick={() => setPage(currentPage + 1)}
                      >
                        {t('compras.pagination-next')}
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </>
          );
        }
        return (
          <NoData>
            <div className="mensaje">
              <p>{t('compras.noRecords')}</p>
            </div>
          </NoData>
        );
      })()}
    </>
  );
};

export default CustomPaginationTable;
